import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { statsAPI } from '../services/api';

export default function DashboardEnhanced() {
  const navigate = useNavigate();
  const { user, logout, hasPaid } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      const response = await statsAPI.getUserStats();
      setStats(response.data);
    } catch (error) {
      console.error('Failed to load stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const overallAccuracy = stats?.totalAnswered > 0
    ? Math.round((stats.totalCorrect / stats.totalAnswered) * 100)
    : 0;

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-600 to-purple-800 p-6">
      <div className="max-w-6xl mx-auto">
        <div className="bg-white rounded-lg shadow-xl p-8">
          {/* Header */}
          <div className="text-center mb-8">
            <h1 className="text-4xl font-bold text-gray-800 mb-2">
              🍁 Canadian Citizenship Test
            </h1>
            <h2 className="text-2xl text-purple-600">
              Welcome, {user?.username}!
            </h2>
          </div>

          {/* Payment Status */}
          <div className="mb-8 text-center">
            <div className={`inline-block px-6 py-3 rounded-full text-lg font-bold ${
              hasPaid ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'
            }`}>
              {hasPaid ? '✅ Premium Member' : '🆓 Free Account'}
            </div>
            {!hasPaid && (
              <button
                onClick={() => navigate('/payment')}
                className="btn btn-primary mt-4"
              >
                Upgrade to Premium - $14.99 CAD
              </button>
            )}
          </div>

          {/* Quick Stats */}
          {!loading && stats && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              <div className="card text-center">
                <div className="text-3xl font-bold text-purple-600">{stats.totalAnswered || 0}</div>
                <div className="text-sm text-gray-600">Questions Answered</div>
              </div>
              <div className="card text-center">
                <div className="text-3xl font-bold text-green-600">{overallAccuracy}%</div>
                <div className="text-sm text-gray-600">Accuracy</div>
              </div>
              <div className="card text-center">
                <div className="text-3xl font-bold text-orange-600">{stats.bestStreak || 0}</div>
                <div className="text-sm text-gray-600">Best Streak</div>
              </div>
              <div className="card text-center">
                <div className="text-3xl font-bold text-blue-600">{stats.totalScore || 0}</div>
                <div className="text-sm text-gray-600">Total Score</div>
              </div>
            </div>
          )}

          {/* Correct/Wrong Breakdown */}
          {!loading && stats && stats.totalAnswered > 0 && (
            <div className="grid grid-cols-2 gap-4 mb-8">
              <div className="card bg-green-50">
                <div className="flex items-center justify-between">
                  <div className="text-4xl">✅</div>
                  <div className="text-right">
                    <div className="text-3xl font-bold text-green-600">{stats.totalCorrect || 0}</div>
                    <div className="text-sm text-gray-600">Correct</div>
                  </div>
                </div>
              </div>
              <div className="card bg-red-50">
                <div className="flex items-center justify-between">
                  <div className="text-4xl">❌</div>
                  <div className="text-right">
                    <div className="text-3xl font-bold text-red-600">{stats.totalWrong || 0}</div>
                    <div className="text-sm text-gray-600">Wrong</div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Main Action - Start Quiz */}
          <div className="mb-8">
            <button
              onClick={() => navigate('/modes')}
              className="btn btn-primary w-full text-2xl py-6"
            >
              🎮 Start Learning →
            </button>
          </div>

          {/* Quick Links */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
            <button
              onClick={() => navigate('/stats')}
              className="btn btn-secondary py-4"
            >
              📊 View Detailed Stats
            </button>
            <button
              onClick={() => navigate('/categories')}
              className="btn btn-secondary py-4"
            >
              📚 Browse Topics
            </button>
            <button
              onClick={() => navigate('/quiz?mode=review')}
              className="btn btn-danger py-4"
            >
              🔄 Review Mistakes
            </button>
          </div>

          {/* Info Box */}
          <div className="card bg-blue-50 border-l-4 border-blue-500 mb-8">
            <div className="flex items-start space-x-3">
              <div className="text-3xl">ℹ️</div>
              <div>
                <div className="font-bold text-blue-900 mb-2">About This Test</div>
                <ul className="text-sm text-blue-800 space-y-1">
                  <li>• 597 questions across 18 topics</li>
                  <li>• Based on "Discover Canada" official study guide</li>
                  <li>• 3 difficulty levels: Easy, Medium, Hard</li>
                  <li>• Track your progress and identify weak areas</li>
                  <li>• Practice with different game modes</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Logout */}
          <div className="text-center">
            <button
              onClick={logout}
              className="btn btn-danger"
            >
              Logout
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
